import { isSymbol } from './strings';

export const getFirstElement = (arr: any[]) => {
  if (!Array.isArray(arr) || !arr.length) return undefined;
  return arr[0];
};

export const getLastElement = (arr: any[]) => {
  if (!Array.isArray(arr) || !arr.length) return undefined;
  return arr[arr.length - 1];
};

const getNestedValue = (obj: { [key: string]: any }, path: string) => {
  if (!path.includes('.')) return obj[path] ?? '';

  return (
    path.split('.').reduce((value, splitKey) => {
      if (value === undefined || value === null) return undefined;
      return value[splitKey];
    }, obj) ?? ''
  );
};

/**
 * Compares two objects by a single key, following the rules documented on
 * {@link sortAlphaNumerically}. Not exported — callers compose keys via
 * `sortAlphaNumerically`, which handles the multi-key fallthrough.
 */
const compareAlphaNumerically = (a: { [key: string]: any }, b: { [key: string]: any }, key: string): number => {
  const valueA = getNestedValue(a, key);
  const valueB = getNestedValue(b, key);

  // Place empty values at the end. Empty values must compare as equal to each other,
  // otherwise the comparator is inconsistent and the sort order becomes unpredictable.
  if (valueA === '' && valueB === '') return 0;
  if (valueA === '') return 1;
  if (valueB === '') return -1;

  const startsWithSymbolA = isSymbol(valueA[0]);
  const startsWithSymbolB = isSymbol(valueB[0]);

  if (startsWithSymbolA && startsWithSymbolB) return valueA.localeCompare(valueB);
  if (startsWithSymbolA) return -1;
  if (startsWithSymbolB) return 1;

  const isNumericA = /^\d+$/.test(valueA);
  const isNumericB = /^\d+$/.test(valueB);

  if (isNumericA && isNumericB) return parseInt(valueA, 10) - parseInt(valueB, 10);
  if (isNumericA) return -1;
  if (isNumericB) return 1;

  return valueA.localeCompare(valueB, undefined, { numeric: true, sensitivity: 'base' });
};

/**
 * Creates a sorting function that handles alphanumeric sorting with special character handling.
 * The sorting follows these rules:
 * 1. Empty values are placed at the end (two empty values compare as equal)
 * 2. Special characters (non-alphanumeric) are sorted first
 * 3. Numbers are sorted numerically
 * 4. Strings are sorted alphabetically (case-insensitive)
 *
 * Pass more than one key to add tiebreakers: each key is applied in turn, falling through to the
 * next only when the current one compares equal. Use this when a single key is not unique enough
 * to produce a predictable order (e.g. first names in a staff list, where duplicates are common).
 *
 * @param firstKey - The object key to sort by. Can be a nested key using dot notation (e.g., 'user.name')
 * @param restKeys - Optional tiebreaker keys, applied in order when earlier keys compare equal
 * @returns A sorting function that can be used with Array.sort()
 *
 * @example
 * // Basic usage
 * const arr = [{ name: 'Bob' }, { name: 'Alice' }];
 * arr.sort(sortAlphaNumerically('name'));
 * // Result: [{ name: 'Alice' }, { name: 'Bob' }]
 *
 * @example
 * // Tiebreaker — equal first names fall through to last name
 * const arr = [{ firstName: 'Alex', lastName: 'Young' }, { firstName: 'Alex', lastName: 'Adams' }];
 * arr.sort(sortAlphaNumerically('firstName', 'lastName'));
 * // Result: [{ firstName: 'Alex', lastName: 'Adams' }, { firstName: 'Alex', lastName: 'Young' }]
 *
 * @example
 * // Special characters come first
 * const arr = [{ name: 'Bob' }, { name: '@Alice' }, { name: 'Charlie' }];
 * arr.sort(sortAlphaNumerically('name'));
 * // Result: [{ name: '@Alice' }, { name: 'Bob' }, { name: 'Charlie' }]
 *
 * @example
 * // Numbers are sorted numerically
 * const arr = [{ name: 'User 10' }, { name: 'User 2' }, { name: 'User 1' }];
 * arr.sort(sortAlphaNumerically('name'));
 * // Result: [{ name: 'User 1' }, { name: 'User 2' }, { name: 'User 10' }]
 *
 * @example
 * // Case insensitive sorting
 * const arr = [{ name: 'bob' }, { name: 'Alice' }, { name: 'Charlie' }];
 * arr.sort(sortAlphaNumerically('name'));
 * // Result: [{ name: 'Alice' }, { name: 'bob' }, { name: 'Charlie' }]
 *
 * @example
 * // Nested object sorting
 * const arr = [{ user: { name: 'Bob' } }, { user: { name: 'Alice' } }];
 * arr.sort(sortAlphaNumerically('user.name'));
 * // Result: [{ user: { name: 'Alice' } }, { user: { name: 'Bob' } }]
 */
export const sortAlphaNumerically =
  (firstKey: string, ...restKeys: string[]) =>
  (a: { [key: string]: any }, b: { [key: string]: any }): number =>
    // Once a key has decided the order, later keys are not compared. Seeding the reduce with the
    // first key keeps the common single-key case allocation-free.
    restKeys.reduce((result, key) => (result !== 0 ? result : compareAlphaNumerically(a, b, key)), compareAlphaNumerically(a, b, firstKey));

/**
 * Creates a sorting function that sorts by a date value at the given key.
 * - Supports dot-notation for nested keys (e.g. 'schedule.start')
 * - Accepts Date instances, ISO/parsable date strings, or epoch numbers
 * - Invalid, missing, null, or unparsable values are placed at the end
 * - Direction defaults to ascending (earliest first)
 *
 * @param key - The object key to sort by. Dot notation supported for nested keys.
 * @param direction - 'asc' (earliest first, default) or 'desc' (latest first).
 * @returns A sorting function suitable for Array.prototype.sort
 *
 * @example
 * const items = [{ start: '2024-02-01' }, { start: '2024-01-01' }];
 * items.sort(sortByDate('start'));
 * // Result: [{ start: '2024-01-01' }, { start: '2024-02-01' }]
 */
export const sortByDate = (key: string, direction: 'asc' | 'desc' = 'asc') => {
  const keys = key.split('.');
  const isNested = keys.length > 1;

  const toTime = (value: unknown): number => {
    if (value === undefined || value === null || value === '') return NaN;
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;
    if (typeof value === 'string') return new Date(value).getTime();
    return NaN;
  };

  return (a: { [key: string]: any }, b: { [key: string]: any }): number => {
    const valA = isNested ? keys.reduce((v, k) => (v == null ? undefined : v[k]), a) : a?.[key];
    const valB = isNested ? keys.reduce((v, k) => (v == null ? undefined : v[k]), b) : b?.[key];

    const timeA = toTime(valA);
    const timeB = toTime(valB);

    const invalidA = Number.isNaN(timeA);
    const invalidB = Number.isNaN(timeB);

    if (invalidA && invalidB) return 0;
    if (invalidA) return 1;
    if (invalidB) return -1;

    return direction === 'desc' ? timeB - timeA : timeA - timeB;
  };
};

export const sumBy = (arr: Record<string, any>[], key: string) => {
  if (!arr || !Array.isArray(arr) || !key) return 0;

  return (arr || []).reduce((acc, curr) => {
    if (!key.includes('.')) {
      if (typeof curr[key] !== 'number') return acc;
      return acc + curr[key];
    }

    const nestedKeys = key.split('.');
    let nestedValue = curr;
    let num = 0;
    for (let i = 0; i < nestedKeys.length; i++) {
      if (i === nestedKeys.length - 1) {
        num = nestedValue[nestedKeys[i]] as number;
      } else {
        nestedValue = nestedValue[nestedKeys[i]];
      }
    }
    return acc + num;
  }, 0);
};
